/* ============================================================
   Hue Synco page controller: a Home Assistant dashboard running the real
   Hue Synco card.

   - The card is hue-synco-card.js, copied unchanged from the integration
     (Hue Synco 1.60.0). It runs against ha-mock.js, a stand-in Home
     Assistant: every entity it reads, service it calls and live feed it
     subscribes to (hue_music_sync/subscribe) is answered here, from the one
     model that also drives the media card, the entities card and the room.
   - The music is real: camusic-engine.js plays the demo clips and analyses
     them in the browser (render:false, its own room is never drawn). From
     that analysis the lamps are dealt the way the integration deals them:
     left to right is low to high in the spectrum, the bass, mid and vocal
     roles trade places every few bars, and the intensity rung, effect,
     colour scheme, brightness ceiling and timing offset all apply. The same
     frame lights the room (oss-room.js), the card's room map and its beat
     pads.
   - Each clip has a track map made offline (sections, tempo, beat phase),
     the way the integration studies a song before it plays; the card draws
     its timeline from it.
   - The player the lights follow maps onto the integration's source ladder:
       Kitchen Sendspin (and Auto, which prefers it) -> live audio
       Lounge AirPlay                               -> the track map
       Bedroom TV, a plain media player             -> metadata only: the
                                                       audio is withheld and
                                                       the room drifts gently
     Picking one on the card moves the song there, as if it had been
     started on that player.

   The song score further down is a separate, self-contained comparison of
   choreography scheduled on a known beat grid against a show that has to
   hear each beat first.
   ============================================================ */

const INT_COPY = {
  auto: 'Scores each song on an absolute scale, picks the band of the ladder it has earned, then moves within that band as the song rises and falls.',
  subtle: 'A gentle spatial gradient, soft colour drift and small beat steps. For lofi, ambient and very soft music.',
  medium: 'Visible dimming, soft flashes on the stronger beats, and a wide spread of colour.',
  high: 'Bass, guitar and vocal roles split across the room, dealt to the instruments actually playing. The everyday rung.',
  intense: 'A unified club swing from dim to bright on the beat, colour shifting with each hit, a soft glow in the gaps.',
  extreme: 'The song as a graph: each lamp shows its own slice of the spectrum, and quiet parts go properly dark.'
};
const WARN = {
  intense: 'Relaxed flash limiter: not suitable for anyone with photosensitivity.',
  extreme: 'Flash limiter bypassed: not suitable for anyone with photosensitivity.'
};
const EFFECT_COPY = {
  movies: 'calm, with a warm cinematic drift. Brightness follows the soundtrack.',
  fireworks: 'bursts ignite on the big beats and fade out, over a dim room.'
};

/* where the music plays, and how the lights hear it: the integration's source
   ladder, from the syncoV2 README */
const PLAYERS = [
  { id: 'media_player.kitchen', name: 'Kitchen Sendspin', mass: true, group: true, src: 'live-tap', clock: 'sendspin', pill: 'Live audio', icon: 'mdi:speaker',
    copy: 'Sendspin: the track’s own stream, decoded in step with the player, with an exact playhead from its synchronised clock.' },
  { id: 'media_player.lounge', name: 'Lounge AirPlay', mass: true, group: true, src: 'track-map', clock: 'ha-state', pill: 'Track map', icon: 'mdi:speaker',
    copy: 'AirPlay players use the pre-analysed track map to drive the show, with full beat accuracy.' },
  { id: 'media_player.bedroom_tv', name: 'Bedroom TV', mass: false, group: false, src: 'metadata', clock: 'ha-state', pill: 'Metadata only', icon: 'mdi:television',
    copy: 'A plain media player: the room glows gently until a stream or track map is available. Analyse the library and it upgrades by itself.' }
];
const AUTO_COPY = 'Auto follows whatever is playing, Music Assistant players first. Right now that is the Kitchen Sendspin player.';

/* the entertainment areas on this bridge; a bridge streams to one at a time */
const AREAS = [
  { id: 'living_room', name: 'Lounge' },
  { id: 'kitchen', name: 'Kitchen' },
  { id: 'bedroom', name: 'Bedroom' }
];
const entsOf = (a) => ({
  sw: 'switch.music_sync_' + a,
  intensity: 'select.music_sync_' + a + '_intensity',
  effect: 'select.music_sync_' + a + '_effect',
  colour: 'select.music_sync_' + a + '_colour',
  brightness: 'number.music_sync_' + a + '_brightness',
  timing: 'number.music_sync_' + a + '_timing_offset'
});

/* each area's lamps, left to right. The integration ranks lamps by x, and that
   rank is the slice of the spectrum each one plays: left low, right high.
   pos is the lamp's place in the entertainment area (-1..1, x, y, height),
   which the card draws its room map from; b is its share of the 24 analysis
   bands (30 Hz - 16 kHz). */
const LAMPS = {
  living_room: [
    { key: 'pendant', pos: [-0.88, 0.1, 0.8], b: [0, 3] },
    { key: 'barL', pos: [-0.62, 0.5, -0.3], b: [3, 7] },
    { key: 'stripL', pos: [-0.38, 0.9, 0.05], b: [7, 10] },
    { key: 'stripT', pos: [0, 0.9, 0.34], b: [10, 14] },
    { key: 'stripR', pos: [0.38, 0.9, 0.05], b: [14, 17] },
    { key: 'barR', pos: [0.62, 0.5, -0.3], b: [17, 21] },
    { key: 'signe', pos: [0.9, 0.2, 0.55], b: [21, 24] }
  ],
  kitchen: [
    { key: 'k1', pos: [-0.6, 0, 0.3], b: [0, 8] },
    { key: 'k2', pos: [0, 0, 0.3], b: [8, 16] },
    { key: 'k3', pos: [0.6, 0, 0.3], b: [16, 24] }
  ],
  bedroom: [
    { key: 'b1', pos: [-0.5, 0.2, -0.1], b: [0, 12] },
    { key: 'b2', pos: [0.5, 0.2, 0.2], b: [12, 24] }
  ]
};
/* the roles dealt left to right (0 bass, 1 mid, 2 vocal), rotated each phrase */
const ROLE_DEAL = { 7: [0, 0, 1, 2, 1, 2, 1], 3: [0, 1, 2], 2: [0, 1] };
/* the integration's five bands (sub-bass, bass, low-mid, mid, high), folded
   from the 24 log-spaced analysis bands */
const BAND5 = [[0, 3], [3, 8], [8, 13], [13, 17], [17, 23]];

const INTENSITIES = ['auto', 'subtle', 'medium', 'high', 'intense', 'extreme'];
const LADDER = ['subtle', 'medium', 'high', 'intense', 'extreme'];
const EFFECTS = ['music', 'movies', 'fireworks'];
const COLOURS = ['album_art', 'album_art_v2', 'song', 'sunset', 'ocean', 'forest', 'lavender', 'ember', 'aurora', 'rainbow', 'tropical', 'savanna', 'blossom', 'honolulu', 'galaxy', 'neon', 'peacock', 'citrus', 'rosegold'];
/* the integration's labels (translations/en.json) */
const TR = {
  mode: { auto: 'Auto', subtle: 'Subtle', medium: 'Medium', high: 'High', intense: 'Intense', extreme: 'Extreme' },
  effect: { music: 'Music', movies: 'Movies', fireworks: 'Fireworks' },
  colour: { album_art: 'Album colours', album_art_v2: 'Album colours v2', song: 'Song colours', sunset: 'Sunset', ocean: 'Ocean', forest: 'Forest', lavender: 'Lavender', ember: 'Ember', aurora: 'Aurora', rainbow: 'Rainbow', tropical: 'Tropical twilight', savanna: 'Savanna sunset', blossom: 'Spring blossom', honolulu: 'Honolulu', galaxy: 'Galaxy', neon: 'Neon', peacock: 'Peacock', citrus: 'Citrus', rosegold: 'Rose gold' }
};
/* the integration's themes, converted from the HSV values in
   custom_components/hue_music_sync/color/palette.py (syncoV2 v1.60.0).
   Song colours come from each song's harmony there; the demo uses the
   card's own swatch for them. */
const THEMES = {
  song: ['#FF5A3C', '#FFD23B', '#27D3FF', '#7B5CFF'],
  sunset: ['#B62BD9', '#FF268E', '#FF2633', '#FF6126', '#FFA133', '#FFCD4D'],
  ocean: ['#40FFD1', '#26FFFF', '#19BAFF', '#1975FF', '#2445F2'],
  forest: ['#90FF4D', '#37FF33', '#24F25E', '#30F2AC'],
  lavender: ['#A073FF', '#C059FF', '#FC66FF', '#FF73C7'],
  ember: ['#FF1927', '#FF3519', '#FF5E19', '#FF9B26', '#FFC633'],
  aurora: ['#33FFC2', '#40FF5E', '#33C2FF', '#7D40FF', '#FF59D1'],
  rainbow: ['#FF1919', '#FF8C19', '#FFFF19', '#27FF26', '#26FFFF', '#2726FF', '#FF26FF'],
  tropical: ['#FF3395', '#FF47ED', '#FF334B', '#FF602E', '#FFB84D'],
  savanna: ['#FF3326', '#FF6726', '#FF8E26', '#FFBA33', '#FFD952'],
  blossom: ['#FF94B4', '#FFB59E', '#FFECAB', '#E3A8FF', '#B8EAFF'],
  honolulu: ['#FF2667', '#FF5A26', '#D633FF', '#33FFFF'],
  galaxy: ['#1923FF', '#6C26FF', '#BA26FF', '#FF38DF'],
  neon: ['#0FF5FC', '#FF2EF0', '#27FF14', '#FF2E73'],
  peacock: ['#1ED4E6', '#1F78FF', '#16D993', '#FFC633'],
  citrus: ['#FFEC3D', '#98EB2F', '#FF971C', '#FF615C'],
  rosegold: ['#FFD0C2', '#FF9A8C', '#E07D5E', '#F5CA5D']
};

/* how each rung moves the lamps: the level they rest at, how far the music
   lifts them, the punch of a kick and of an onset, and how fast they fall */
const RUNG = {
  subtle: { floor: 0.46, gain: 0.34, kick: 0.08, pop: 0.1, rel: 0.7, flow: 0.05 },
  medium: { floor: 0.32, gain: 0.56, kick: 0.26, pop: 0.3, rel: 0.4, flow: 0.08 },
  high: { floor: 0.2, gain: 0.78, kick: 0.52, pop: 0.55, rel: 0.24, flow: 0.12, roles: true },
  intense: { floor: 0.1, gain: 0.9, kick: 0.8, pop: 0.7, rel: 0.15, flow: 0.2, swing: true, jump: true },
  extreme: { floor: 0, gain: 1.1, kick: 1, pop: 0.9, rel: 0.09, flow: 0.3, dark: true, chase: true }
};
/* the room's own scene while nothing streams to it: warm white, 2700 K */
const SCENE = [255, 187, 122];

const hexRgb = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const rgbHex = (c) => '#' + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
const mixC = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const full = (c) => { const m = Math.max(c[0], c[1], c[2], 1); return c.map((v) => v * 255 / m); };
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
/* a soft shoulder: 0..1 in, 0..1 out, bright moments compressed not clipped */
const tone = (v) => Math.min(1, (1 - Math.exp(-2.3 * Math.max(0, v))) / (1 - Math.exp(-2.3)));
const r2 = (v) => Math.round(v * 100) / 100;
const r3 = (v) => Math.round(v * 1000) / 1000;

/* ---------- the score: one song, known in advance ---------- */
const SONG = { len: 32, beat: 0.5 };
const SECTIONS = [
  { name: 'Verse', a: 0, b: 8, e0: 0.34, e1: 0.40 },
  { name: 'Build', a: 8, b: 13, e0: 0.42, e1: 0.78 },
  { name: 'Drop', a: 13, b: 19.5, e0: 0.98, e1: 0.9, hot: true },
  { name: 'Verse', a: 19.5, b: 26, e0: 0.4, e1: 0.44 },
  { name: 'Finale', a: 26, b: 32, e0: 0.92, e1: 0.86, hot: true }
];
const REACT_LAG = 0.16;   /* illustrative: hearing a beat before flashing on it */
function sectionAt(t) { for (const s of SECTIONS) if (t < s.b) return s; return SECTIONS[SECTIONS.length - 1]; }
function energyAt(t) { const s = sectionAt(t), u = Math.max(0, Math.min(1, (t - s.a) / (s.b - s.a))); return s.e0 + (s.e1 - s.e0) * u; }

/* The living room TV, showing the music the way a Music Assistant or Apple TV
   now-playing screen does: the cover over a blur of itself, the title and the
   progress. Painted a few times a second into an offscreen canvas that the
   room maps onto the set. The blur is the cover squeezed to 32 x 18 and
   stretched back up: no canvas filter, so it looks the same in Safari. */
class TvScreen {
  constructor() {
    this.c = document.createElement('canvas');
    this.c.width = 640; this.c.height = 360;
    this.small = document.createElement('canvas');
    this.small.width = 32; this.small.height = 18;
    this.ready = false;
    this.light = [40, 40, 44];
  }

  setTrack(tr, where) {
    this.where = where;
    if (!tr || tr.cover === this.url) return;
    this.url = tr.cover;
    this.tr = tr;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (this.url !== tr.cover) return;
      this.img = img;
      const sc = this.small.getContext('2d', { willReadFrequently: true });
      const iw = img.naturalWidth, ih = img.naturalHeight;
      sc.drawImage(img, 0, ih * 0.22, iw, ih * 0.56, 0, 0, 32, 18);
      try {
        const d = sc.getImageData(0, 0, 32, 18).data;
        let r = 0, g = 0, b = 0;
        for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; }
        const n = d.length / 4;
        /* what the darkened screen throws into the room */
        this.light = [r / n * 0.62, g / n * 0.62, b / n * 0.62];
      } catch (e) { this.light = [60, 60, 64]; }
      this.ready = true;
      this.paint(this.t || 0, this.d || 0, this.playing);
    };
    img.src = tr.cover;
  }

  paint(t, d, playing) {
    this.t = t; this.d = d; this.playing = playing;
    if (!this.img) return;
    const ctx = this.c.getContext('2d'), W = 640, H = 360, tr = this.tr;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.small, 0, 0, W, H);
    ctx.fillStyle = 'rgba(0,0,0,0.42)';
    ctx.fillRect(0, 0, W, H);
    const g = ctx.createRadialGradient(W * 0.5, H * 0.5, H * 0.2, W * 0.5, H * 0.5, W * 0.62);
    g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,0.5)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    const cs = 196, cx = 74, cy = 72;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 30; ctx.shadowOffsetY = 12;
    ctx.fillStyle = '#000'; ctx.fillRect(cx, cy, cs, cs);
    ctx.restore();
    ctx.drawImage(this.img, cx, cy, cs, cs);
    const tx = cx + cs + 36, tw = W - tx - 56;
    const fit = (text, font, max) => {
      ctx.font = font;
      let s2 = String(text || '');
      while (ctx.measureText(s2).width > max && s2.length > 2) s2 = s2.slice(0, -2) + '…';
      return s2;
    };
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillText(fit((this.where || '').toUpperCase(), '500 13px Roboto, "Segoe UI", sans-serif', tw), tx, cy + 34);
    ctx.fillStyle = '#fff';
    ctx.fillText(fit(tr.label, '700 32px Roboto, "Segoe UI", sans-serif', tw), tx, cy + 82);
    ctx.fillStyle = 'rgba(255,255,255,0.72)';
    ctx.fillText(fit(tr.artist, '400 20px Roboto, "Segoe UI", sans-serif', tw), tx, cy + 114);
    const py = cy + cs - 34;
    ctx.fillStyle = 'rgba(255,255,255,0.22)'; ctx.fillRect(tx, py, tw, 4);
    ctx.fillStyle = '#fff'; ctx.fillRect(tx, py, d ? tw * Math.min(1, t / d) : 0, 4);
    const mm = (v) => { v = Math.max(0, Math.floor(v || 0)); return Math.floor(v / 60) + ':' + String(v % 60).padStart(2, '0'); };
    ctx.font = '500 14px Roboto, "Segoe UI", sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText(mm(t), tx, py + 26);
    ctx.textAlign = 'right';
    ctx.fillText(mm(d), tx + tw, py + 26);
    ctx.textAlign = 'left';
    if (!playing) {
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillRect(tx, cy + 136, 6, 20);
      ctx.fillRect(tx + 11, cy + 136, 6, 20);
    }
  }
}

const INSTANCES = new WeakMap();

class HueSyncoPage {
  constructor(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.tracks = cfg.tracks || [];
    this.maps = cfg.maps || {};
    this.refs = {};
    this.listeners = [];
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.trackIdx = 0;
    this.playing = false;
    this.loading = false;
    this.playerId = PLAYERS[0].id;
    this.pinned = null;
    this.volume = 0.8;
    this.groups = { 'media_player.kitchen': ['media_player.kitchen'], 'media_player.lounge': ['media_player.lounge'] };
    const lv = INTENSITIES.includes(cfg.defaultIntensity) ? cfg.defaultIntensity : 'high';
    this.set = {};
    AREAS.forEach((A, i) => {
      this.set[A.id] = { intensity: i ? 'medium' : lv, effect: 'music', colour: 'album_art', brightness: 100, timing: 0, autoLevels: ['subtle', 'medium', 'high'], autoTiming: false, advanced: false, tunables: {} };
    });
    this.syncOn = { living_room: true, kitchen: false, bedroom: false };
    this.entIndex = {};
    AREAS.forEach((A) => { const I = entsOf(A.id); Object.keys(I).forEach((k) => { this.entIndex[I[k]] = { area: A.id, key: k }; }); });
    this.level = 'medium';                 /* the rung the engine's Auto sits on */
    this.anchor = { position: 0, at: Date.now() };

    /* the analysis, latched between frames */
    this.bands = null; this.energy = 0; this.kickIn = false; this.kickStrength = 0.6;
    this.beatLatch = false; this.strengthLatch = 0;
    /* the show */
    this.lamp = Array.from({ length: 7 }, () => ({ env: 0, slow: 0, col: SCENE.slice(), burst: 0, burstCol: SCENE, tap: 0 }));
    this.kickPulse = 0; this.kicks = 0; this.rot = 0; this.roleShift = 0; this.chaseT = 9; this.chaseDir = 1; this.movieEnv = 0.3;
    this.frames = [];
    this.show = null;
    this.roomL = LAMPS.living_room.map(() => SCENE.map((c) => c * 0.4));
    this.drumUntil = 0;
    this.tv = new TvScreen();
    this.tvAt = 0;
    this.subs = new Set();
    this.lastStream = 0; this.lastMeta = 0;
    this.statusFlash = null;

    /* score */
    this.sMode = 'scheduled'; this.sT = 0; this.sPlay = false;
    this.sLamps = new Array(9).fill(0); this.sFlashes = []; this.sEnvTrack = 0.3;
  }

  on(t, ty, fn, o) { t.addEventListener(ty, fn, o); this.listeners.push([t, ty, fn, o]); }
  importSpec(spec) { return import(/^(https?:)?\/\//.test(spec) ? spec : new URL(spec, document.baseURI || location.href).href); }

  mount() {
    this.root.querySelectorAll('[data-ref]').forEach((el) => { this.refs[el.getAttribute('data-ref')] = el; });
    this.on(this.root, 'click', (e) => {
      const el = e.target.closest('[data-act]');
      if (!el || !this.root.contains(el)) return;
      const fn = HueSyncoPage.ACTIONS[el.getAttribute('data-act')];
      if (fn) fn.call(this, el, e);
    });
    this.renderMedia();
    this.renderStatus();
    this.renderNote();
    this.renderRoomNote();

    /* the analyser: the engine, with nothing of its own drawn */
    this.importSpec(this.cfg.modules.engine).then((mod) => {
      this.demo = mod.createRoomDemo({ tracks: this.tracks, render: false, onState: (s) => this.onDemoState(s) });
      this.demo.setVolume(this.volume);
      this.applyIntensity();
      this.applyTiming();
    }).catch((err) => {
      this.engineStatus = 'The audio engine failed to load';
      this.renderStatus();
      console.error('[hue-synco] engine failed to load', err);
    });

    /* the stand-in Home Assistant, at once; the card itself on approach */
    if (this.cfg.ha && this.cfg.ha.mock) {
      this.importSpec(this.cfg.ha.mock).then((HA) => this.startHa(HA))
        .catch((err) => console.error('[hue-synco] the Home Assistant stand-in failed to load', err));
    }

    this.visible = true;
    if ('IntersectionObserver' in window && this.refs.hass) {
      this.io = new IntersectionObserver((ents) => { this.visible = ents[0].isIntersecting; }, { rootMargin: '80px 0px' });
      this.io.observe(this.refs.hass);
    }
    this.importSpec(this.cfg.modules.room).then((mod) => {
      this.M = mod;
      this.room = new mod.Room(this.refs.stage, { pendant: true });
      this.on(window, 'resize', () => { this.room.resize(); this.fitScore(); this.drawScore(); });
    }).catch((err) => console.error('[hue-synco] room failed to load', err));

    this.fitScore();
    this.drawScore();
    this.last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      if (this.visible && !document.hidden) {
        this.stepShow(dt, now / 1000);
        if (this.room) this.drawRoom(dt);
        this.renderProgress();
        if (now - this.tvAt > 250) {
          this.tvAt = now;
          const m = this.demo && this.demo.media();
          this.tv.paint(m ? m.t : 0, m && m.d ? m.d : (this.maps[this.track().id] || {}).duration || 0, this.playing);
        }
        this.feed(now);
        this.watchClock();
      }
      if (this.sPlay) { this.stepScore(dt); this.drawScore(); }
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
    this.wireScoreSeek();
  }

  destroy() {
    this.listeners.forEach(([t, ty, fn, o]) => t.removeEventListener(ty, fn, o));
    this.listeners = [];
    if (this.demo) this.demo.destroy();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.io) this.io.disconnect();
    if (this.cardIo) this.cardIo.disconnect();
    if (this.ro) this.ro.disconnect();
    if (this.ents) this.ents.destroy();
    clearTimeout(this.statusT);
    this.subs.clear();
  }

  /* ================= the model ================= */

  cur() { return PLAYERS.find((p) => p.id === this.playerId) || PLAYERS[0]; }
  track() { return this.tracks[this.trackIdx] || this.tracks[0]; }
  areaOn() { for (const A of AREAS) if (this.syncOn[A.id]) return A.id; return null; }
  fed() { return this.playing && this.cur().src !== 'metadata'; }
  pos() {
    const m = this.demo && this.demo.media();
    return m ? m.t : this.anchor.position;
  }

  /* the rung playing now: Auto resolves to one of its enabled rungs; a device
     that asks for reduced motion never gets the club rungs in the room */
  rungNow(area) {
    const S = this.set[area];
    let r = S.intensity === 'auto' ? this.autoRung(area) : S.intensity;
    if (this.reduced && (r === 'intense' || r === 'extreme')) r = 'high';
    return r;
  }
  autoRung(area) {
    const levels = this.set[area].autoLevels;
    if (levels.includes(this.level)) return this.level;
    const want = LADDER.indexOf(this.level);
    let best = levels[0], bd = 9;
    levels.forEach((l) => { const d = Math.abs(LADDER.indexOf(l) - want); if (d < bd) { bd = d; best = l; } });
    return best || 'medium';
  }

  palette(key) {
    const tr = this.track();
    const sw = key === 'album_art' || key === 'album_art_v2' ? tr.sw : THEMES[key] || tr.sw;
    const k = key + '|' + tr.id;
    if (this.palKey !== k) { this.palKey = k; this.pal = sw.map((h) => full(hexRgb(h))); }
    return this.pal;
  }

  roleOf(i, n) {
    const deal = ROLE_DEAL[n] || ROLE_DEAL[7];
    return deal[(i + this.roleShift) % n];
  }

  /* ================= the show ================= */

  stepShow(dt, t) {
    const area = this.areaOn();
    const lamps = LAMPS[area || 'living_room'], n = lamps.length;
    const S = this.set[area || 'living_room'];
    const tun = S.advanced ? S.tunables : {};
    const k = (key) => (tun[key] == null ? 1 : tun[key]);
    const now = performance.now();
    const drum = now < this.drumUntil;
    const fed = !!(area && this.fed() && this.bands);
    const pal = this.palette(S.colour), np = pal.length;
    const R = RUNG[this.rungNow(area || 'living_room')];
    const effect = S.effect;

    /* the beat: a decaying pulse off the engine's kick detector, paused while
       the beat pads have the room */
    if (this.kickIn && fed && !drum) {
      this.kickPulse = Math.max(this.kickPulse, this.kickStrength);
      this.kicks++;
      this.chaseT = 0;
      this.chaseDir = -this.chaseDir;
      if (R.jump && effect === 'music') this.rot++;
      if (this.kicks % 16 === 0) { this.rot++; this.roleShift++; }
      if (effect === 'fireworks' && this.kickStrength > 0.5) {
        const shots = this.kickStrength > 0.8 && !this.reduced ? 2 : 1;
        for (let s = 0; s < shots; s++) {
          const L = this.lamp[Math.floor(Math.random() * n)];
          L.burst = 1;
          L.burstCol = pal[Math.floor(Math.random() * np)];
        }
      }
    }
    this.kickIn = false;
    this.kickPulse *= Math.exp(-dt / 0.14);
    this.chaseT += dt * k('movement');
    this.movieEnv += ((fed ? this.energy : 0.32) - this.movieEnv) * (1 - Math.exp(-dt / 1.6));
    const chase = (i) => {
      if (!R.chase || this.reduced) return 0;
      const idx = this.chaseDir > 0 ? i : n - 1 - i, p = this.chaseT / 0.045;
      return Math.exp(-((p - idx) * (p - idx)) / 0.8);
    };

    const out = [];
    const bright = S.brightness / 100;
    for (let i = 0; i < n; i++) {
      const L = this.lamp[i], role = this.roleOf(i, n);
      const slot = (i + this.rot) % np;
      let target = pal[slot], v;
      let e = 0;
      if (fed) {
        const [a, b] = lamps[i].b;
        for (let j = a; j < b; j++) e += this.bands[j] || 0;
        e = Math.pow(clamp(e / (b - a), 0, 1.25), k('contrast'));
      }
      L.slow += (e - L.slow) * (1 - Math.exp(-dt / 0.45));
      const pop = Math.max(0, e - L.slow * 1.08);
      const punch = k('reactivity'), glow = k('glow');
      if (!area) {
        v = 0.5;
        target = SCENE;
      } else if (effect === 'movies') {
        v = 0.3 + 0.55 * this.movieEnv;
        target = mixC(mixC(pal[slot], pal[(slot + 1) % np], 0.5 + 0.5 * Math.sin(t * 0.2 + i * 1.1)), [255, 168, 92], 0.42);
      } else if (effect === 'fireworks') {
        v = 0.08 + 0.18 * (fed ? this.energy : 0.3);
        if (L.burst > 0.02) {
          v = Math.max(v, L.burst * 1.15 * (this.reduced ? 1 : 0.88 + 0.12 * Math.sin(t * 38 + i * 3)));
          target = L.burstCol;
        }
      } else if (!fed) {
        /* metadata only, or waiting for the music: a gentle glow, no guessing */
        const p = area && this.playing;
        v = p ? 0.34 + 0.12 * Math.sin(t * 0.7 + i * 1.9) : 0.26 + 0.07 * Math.sin(t * 0.5 + i * 2.3);
        target = mixC(pal[slot], pal[(slot + 1) % np], 0.5 + 0.5 * Math.sin(t * (p ? 0.25 : 0.16) + i));
      } else {
        if (R.swing) {
          v = R.floor * glow + R.gain * glow * (0.55 * this.energy + 0.45 * e) + this.kickPulse * R.kick * punch + pop * R.pop * punch;
        } else if (R.dark) {
          v = Math.pow(e, 1.5) * R.gain * glow + chase(i) * R.kick * punch + pop * R.pop * punch * 0.8;
        } else {
          v = R.floor * glow + R.gain * glow * e;
          if (R.roles) {
            if (role === 0) v += this.kickPulse * R.kick * 1.15 * punch + pop * R.pop * 0.5 * punch;
            else if (role === 1) v += pop * R.pop * 2 * punch + this.kickPulse * R.kick * 0.25 * punch;
            else v = R.floor * 0.75 * glow + R.gain * 0.6 * glow * e + 0.05 * Math.sin(t * 9 + i * 2);
          } else {
            v += this.kickPulse * R.kick * punch * (1 - 0.5 * i / Math.max(1, n - 1)) + pop * R.pop * 1.4 * punch;
          }
        }
        target = mixC(pal[slot], pal[(slot + 1) % np], 0.5 + 0.5 * Math.sin(t * R.flow * k('colour_speed') * 2.4 + i * 1.3));
      }
      L.burst *= Math.exp(-dt / 0.55);
      /* beat pads: a third of the room each, at full brightness */
      if (L.tap > 0.01) { v = Math.max(v, L.tap * 1.2); target = mixC(target, [255, 255, 255], 0.12 * L.tap); }
      L.tap *= Math.exp(-dt / 0.2);
      /* envelope: a fast rise, the rung's own fall */
      const rel = effect === 'movies' ? 1.2 : effect === 'fireworks' ? 0.25 : fed ? R.rel : 0.6;
      L.env += (v - L.env) * (1 - Math.exp(-dt / (v > L.env ? 0.03 : rel)));
      const glide = fed && R.jump && effect === 'music' ? 0.07 : 0.3 / k('colour_speed');
      L.col = mixC(L.col, target, 1 - Math.exp(-dt / Math.max(0.03, glide)));
      const lvl = tone(L.env) * (area ? bright : 1);
      out.push([L.col[0] * lvl, L.col[1] * lvl, L.col[2] * lvl]);
    }

    /* the timing offset: later lights come off a short history of frames;
       earlier ones hold the sound back instead (applyTiming) */
    this.frames.push({ t: now, lamps: out });
    while (this.frames.length > 2 && this.frames[1].t < now - 620) this.frames.shift();
    const d = area ? Math.max(0, S.timing) : 0;
    let f = this.frames[this.frames.length - 1];
    if (d > 0) {
      f = this.frames[0];
      for (let j = this.frames.length - 1; j >= 0; j--) if (this.frames[j].t <= now - d) { f = this.frames[j]; break; }
    }
    this.show = area ? f.lamps : null;
  }

  /* the living room: the show when it streams here, its own scene otherwise */
  drawRoom(dt) {
    const lamps = LAMPS.living_room, showing = this.areaOn() === 'living_room' && this.show;
    const L = {};
    const tau = showing ? 0.016 : 0.45;
    lamps.forEach((l, i) => {
      const target = showing ? this.show[i] : SCENE.map((c) => c * 0.42);
      this.roomL[i] = mixC(this.roomL[i], target, 1 - Math.exp(-dt / tau));
      L[l.key] = this.roomL[i];
    });
    /* the Signe gradient's two tones: its own colour low, its neighbour's up top */
    L.signeTop = mixC(this.roomL[6], this.roomL[5], 0.4);
    /* the TV shows what is playing, and lights the room the way a screen does */
    const tv = this.tv.ready ? this.tv : null;
    this.room.draw({ lamps: L, tv: tv ? (ctx, x, y, w, h) => ctx.drawImage(tv.c, x, y, w, h) : null, tvLight: tv ? tv.light : null });
  }

  /* the card's live feed: stream frames at 20 Hz, the slow picture at 1 Hz */
  feed(now) {
    if (!this.subs.size) return;
    const area = this.areaOn();
    if (!area) return;
    const sw = entsOf(area).sw;
    if (now - this.lastStream >= 50) {
      this.lastStream = now;
      const lamps = LAMPS[area], lights = {}, roles = {};
      lamps.forEach((l, i) => {
        lights[String(i)] = rgbHex(this.show ? this.show[i] : [0, 0, 0]);
        roles[String(i)] = this.roleOf(i, lamps.length);
      });
      const fed = this.fed() && this.bands;
      const bands = BAND5.map(([a, b]) => {
        if (!fed) return 0;
        let s = 0;
        for (let j = a; j < b; j++) s += this.bands[j] || 0;
        return r3(clamp(s / (b - a), 0, 1));
      });
      const drum = performance.now() < this.drumUntil;
      const ev = { type: 'stream', lights, roles, bands, energy: r3(fed ? this.energy : 0), beat: !!(fed && this.beatLatch && !drum), strength: r2(this.strengthLatch * 2) };
      this.beatLatch = false;
      this.strengthLatch = 0;
      this.subs.forEach((s) => { if (s.entity === sw) s.send(ev); });
    }
    if (now - this.lastMeta >= 1000) {
      this.lastMeta = now;
      const m = this.metaEvent(area);
      this.subs.forEach((s) => { if (s.entity === sw) s.send(m); });
    }
  }

  metaEvent(area) {
    const positions = {}, tr = this.track(), map = this.maps[tr.id] || {}, P = this.cur(), S = this.set[area];
    LAMPS[area].forEach((l, i) => { positions[String(i)] = l.pos; });
    const m = { type: 'meta', positions, playing: this.playing, position: r2(this.pos()),
      clock: { source: P.clock, confidence: P.clock === 'sendspin' ? 1 : 0.7 }, timing: { applied_ms: S.timing, auto: S.autoTiming } };
    if (map.duration) m.duration = map.duration;
    /* the track map: none for a plain player, which is the point of it */
    if (P.src !== 'metadata' && map.sections) m.sections = map.sections;
    if (P.src !== 'metadata' && map.bpm && tr.id !== 't4') m.bpm = map.bpm;
    return m;
  }

  /* keep the published playhead honest: re-anchor on a loop, a stall or a seek */
  watchClock() {
    const m = this.demo && this.demo.media();
    if (!m) return;
    const want = this.playing ? this.anchor.position + (Date.now() - this.anchor.at) / 1000 : this.anchor.position;
    if (Math.abs(m.t - want) > 0.6) this.reanchor(m.t);
  }
  reanchor(t) {
    this.anchor = { position: Math.round(t * 100) / 100, at: Date.now() };
    this.syncHa();
  }

  applyIntensity() {
    if (this.demo) this.demo.setIntensity(this.set[this.areaOn() || 'living_room'].intensity);
  }
  applyTiming() {
    const S = this.set[this.areaOn() || 'living_room'];
    if (this.demo) this.demo.setAudioDelay(Math.max(0, -S.timing) / 1000);
  }

  /* ================= Home Assistant ================= */

  startHa(HA) {
    this.HA = HA;
    HA.ensureElements();
    const reg = {}, devices = {};
    AREAS.forEach((A) => {
      const I = entsOf(A.id), dev = 'music_sync_' + A.id;
      devices[dev] = { id: dev, name: 'Music Sync · ' + A.name, name_by_user: null, manufacturer: 'Signify (Philips Hue)', model: 'Entertainment area' };
      const add = (id, key) => { reg[id] = { entity_id: id, platform: 'hue_music_sync', translation_key: key, device_id: dev, has_entity_name: true, name: null, icon: null, entity_category: null, hidden: false }; };
      add(I.sw, null); add(I.intensity, 'mode'); add(I.effect, 'effect'); add(I.colour, 'colour'); add(I.brightness, 'brightness'); add(I.timing, 'timing');
    });
    this.mock = new HA.MockHass({
      states: this.haStates(),
      entities: reg,
      devices,
      translations: { mode: TR.mode, effect: TR.effect, colour: TR.colour },
      onService: (domain, service, data) => this.haService(domain, service, data),
      onMessage: (msg) => this.haMessage(msg),
      onSubscribe: (msg, send) => this.haSubscribe(msg, send)
    });
    const I = entsOf('living_room');
    this.ents = new HA.EntitiesCard(this.refs.entRows, this.mock, [
      { id: I.sw, name: 'Lounge', kind: 'toggle' },
      { id: I.intensity, name: 'Intensity', kind: 'select' },
      { id: I.effect, name: 'Effect', kind: 'select' },
      { id: I.colour, name: 'Colour', kind: 'select' },
      { id: I.brightness, name: 'Brightness', kind: 'slider' },
      { id: I.timing, name: 'Timing offset', kind: 'slider' },
      { id: I.sw, name: 'Clock source', kind: 'text', attr: 'clock_source', icon: 'mdi:clock-outline' }
    ]);
    /* the card itself: loaded when the dashboard comes near */
    const cell = this.refs.syncoCell;
    if (!cell || !this.cfg.ha.card) return;
    const load = () => HA.loadCards(new URL(this.cfg.ha.card, document.baseURI).href, ['hue-music-sync-card', 'hue-music-sync-card-tablet'])
      .then(() => this.mountCard())
      .catch((err) => { console.error('[hue-synco] the card failed to load', err); cell.classList.add('is-failed'); });
    if (!('IntersectionObserver' in window)) { load(); return; }
    this.cardIo = new IntersectionObserver((ents) => {
      if (!ents.some((e) => e.isIntersecting)) return;
      this.cardIo.disconnect();
      this.cardIo = null;
      load();
    }, { rootMargin: '800px 0px' });
    this.cardIo.observe(cell);
  }

  /* the tablet card where there is room for its two columns, the phone card
     where there is not; swapped live as the column changes width */
  mountCard() {
    const cell = this.refs.syncoCell;
    const make = () => {
      const tag = cell.clientWidth >= 640 ? 'hue-music-sync-card-tablet' : 'hue-music-sync-card';
      if (this.card && this.card.localName === tag) return;
      if (this.card) this.mock.detach(this.card);
      const el = document.createElement(tag);
      /* The card's timing row (trim, beat pads, library, speaker group) needs
         more room than half a phone-width card gives it, and clips. Stack
         brightness over timing there, the way a card-mod style would: an
         adopted sheet, which the card's own re-renders leave in place. The
         card file itself stays exactly as the integration ships it. */
      try {
        if (!this.narrowSheet && window.CSSStyleSheet && 'adoptedStyleSheets' in Document.prototype) {
          this.narrowSheet = new CSSStyleSheet();
          this.narrowSheet.replaceSync(':host { container-type: inline-size; } @container (max-width: 520px) { .hue-grid2.tight:not(.hue-selects) { grid-template-columns: minmax(0, 1fr); } }');
        }
        if (this.narrowSheet) el.shadowRoot.adoptedStyleSheets = [this.narrowSheet];
      } catch (e) { /* an older browser: the row clips, as it would in HA */ }
      el.setConfig(this.cardConfig());
      this.mock.attach(el);
      cell.replaceChildren(el);
      cell.classList.add('is-live');
      this.card = el;
    };
    make();
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => make());
      this.ro.observe(cell);
    }
  }

  cardConfig() {
    return {
      type: 'custom:hue-music-sync-card',
      areas: AREAS.map((A) => {
        const I = entsOf(A.id);
        return { name: A.name, switch: I.sw, intensity: I.intensity, effect: I.effect, colour: I.colour, brightness: I.brightness, timing: I.timing, media_player: this.playerId };
      })
    };
  }

  syncHa() { if (this.mock) this.mock.replace(this.haStates()); }

  playerState(p) {
    if (p.id === this.playerId) return this.playing ? 'playing' : 'paused';
    return p.mass ? 'idle' : 'off';
  }

  /* every entity the dashboard shows, read off the model */
  haStates() {
    const out = {}, P = this.cur(), tr = this.track(), map = this.maps[tr.id] || {};
    const at = new Date(this.anchor.at).toISOString();
    const locked = map.bpm && tr.id !== 't4' && P.src !== 'metadata';
    AREAS.forEach((A) => {
      const I = entsOf(A.id), S = this.set[A.id], on = !!this.syncOn[A.id], dev = 'Music Sync · ' + A.name;
      const a = { advanced: S.advanced, media_player: this.pinned, auto_levels: S.autoLevels.slice(), auto_timing: S.autoTiming, icon: 'mdi:music-note', friendly_name: dev };
      if (S.advanced) a.tunables = Object.assign({}, S.tunables);
      if (on) {
        a.audio_source = P.src;
        a.album_colors = tr.sw.slice();
        if (locked) { a.bpm = Math.round(map.bpm); a.beat_anchor = map.anchor; }
        if (S.intensity === 'auto') a.auto_mode = this.autoRung(A.id);
        if (S.autoTiming) { a.auto_timing_mode = P.src === 'metadata' ? 'n/a' : 'tracking'; a.timing_locked = true; }
        a.timing_applied_ms = Math.round(S.timing / 10) * 10;
        a.clock_source = P.clock;
        const sec = (map.sections || []).find((x) => this.anchor.position >= x[0] && this.anchor.position < x[1]);
        if (sec && P.src !== 'metadata') a.section_energy = sec[2];
        a.media_title = tr.label;
        a.media_artist = tr.artist;
        a.media_image = tr.cover;
        a.media_position = this.anchor.position;
        a.media_position_updated_at = at;
        a.source_player = P.id;
      }
      out[I.sw] = { state: on ? 'on' : 'off', attributes: a };
      out[I.intensity] = { state: S.intensity, attributes: { options: INTENSITIES.slice(), icon: 'mdi:sine-wave', friendly_name: dev + ' Intensity' } };
      out[I.effect] = { state: S.effect, attributes: { options: EFFECTS.slice(), icon: 'mdi:auto-fix', friendly_name: dev + ' Effect' } };
      out[I.colour] = { state: S.colour, attributes: { options: COLOURS.slice(), icon: 'mdi:palette', friendly_name: dev + ' Colour' } };
      out[I.brightness] = { state: S.brightness, attributes: { min: 5, max: 100, step: 5, mode: 'slider', unit_of_measurement: '%', icon: 'mdi:brightness-6', friendly_name: dev + ' Brightness' } };
      out[I.timing] = { state: S.timing, attributes: { min: -500, max: 500, step: 10, mode: 'slider', unit_of_measurement: 'ms', icon: 'mdi:timer-sync-outline', friendly_name: dev + ' Timing offset' } };
    });
    PLAYERS.forEach((p) => {
      const here = p.id === P.id;
      const a = { friendly_name: p.name, icon: p.icon, volume_level: here ? this.volume : 0.5, is_volume_muted: false, supported_features: p.group ? 3983295 : 21437 };
      if (p.group) a.group_members = (this.groups[p.id] || [p.id]).slice();
      if (p.mass) a.app_name = 'Music Assistant';
      if (here) Object.assign(a, { media_content_type: 'music', media_title: tr.label, media_artist: tr.artist, media_album_name: tr.label, media_duration: map.duration || null, media_position: this.anchor.position, media_position_updated_at: at, entity_picture: tr.cover });
      out[p.id] = { state: this.playerState(p), attributes: a };
    });
    return out;
  }

  haService(domain, service, data) {
    const id = [].concat(data.entity_id || [])[0] || '';
    const hit = domain === 'switch' || domain === 'select' || domain === 'number' ? this.entIndex[id] : null;
    if (hit) {
      const S = this.set[hit.area], A = AREAS.find((x) => x.id === hit.area);
      if (hit.key === 'sw') {
        const on = service === 'toggle' ? !this.syncOn[hit.area] : service === 'turn_on';
        this.setArea(hit.area, on);
        return true;
      }
      if (domain === 'select' && data.option != null) {
        S[hit.key] = data.option;
        if (hit.key === 'intensity') this.applyIntensity();
        this.flashStatus(A.name + ': ' + (hit.key === 'intensity' ? 'intensity' : hit.key) + ' set to ' + (TR[hit.key === 'intensity' ? 'mode' : hit.key][data.option] || data.option));
      } else if (domain === 'number' && data.value != null) {
        if (hit.key === 'brightness') S.brightness = clamp(Math.round(Number(data.value) / 5) * 5, 5, 100);
        if (hit.key === 'timing') { S.timing = clamp(Math.round(Number(data.value) / 10) * 10, -500, 500); this.applyTiming(); }
        this.flashStatus(A.name + ': ' + (hit.key === 'timing' ? 'timing offset ' + (S.timing > 0 ? '+' : '') + S.timing + ' ms' : 'brightness ' + S.brightness + '%'));
      }
      this.renderNote();
      this.syncHa();
      return true;
    }
    if (domain === 'media_player') {
      if (service === 'media_play_pause' || service === 'toggle') this.togglePlay();
      else if (service === 'media_play') { if (this.demo && !this.demo.playing) this.demo.play(); }
      else if (service === 'media_pause' || service === 'media_stop') { if (this.demo) this.demo.pause(); }
      else if (service === 'media_next_track') this.selectTrack(this.trackIdx + 1);
      else if (service === 'media_previous_track') this.selectTrack(this.trackIdx - 1);
      else if (service === 'media_seek' && data.seek_position != null) {
        if (this.demo) this.demo.seek(Number(data.seek_position));
        this.reanchor(Number(data.seek_position));
      } else if (service === 'volume_set' && data.volume_level != null) {
        this.volume = clamp(Number(data.volume_level), 0, 1);
        if (this.demo) this.demo.setVolume(this.volume);
        this.syncHa();
      } else if (service === 'join' && data.group_members) {
        const g = this.groups[id] || [id];
        [].concat(data.group_members).forEach((m) => { if (!g.includes(m)) g.push(m); });
        this.groups[id] = g;
        this.syncHa();
      } else if (service === 'unjoin') {
        Object.keys(this.groups).forEach((lead) => { if (lead !== id) this.groups[lead] = this.groups[lead].filter((m) => m !== id); });
        this.syncHa();
      }
      return true;
    }
    if (domain === 'hue_music_sync' && service === 'set_options') {
      const area = (this.entIndex[data.entity_id] || { area: 'living_room' }).area, S = this.set[area];
      if (data.auto_levels) S.autoLevels = LADDER.filter((r) => data.auto_levels.includes(r));
      if (data.auto_timing != null) S.autoTiming = !!data.auto_timing;
      if (data.advanced != null) S.advanced = !!data.advanced;
      if (data.tunables) S.tunables = Object.assign({}, data.tunables);
      if (data.media_player != null) {
        this.pinned = data.media_player || null;
        this.movePlayer(this.pinned || PLAYERS[0].id);
      }
      this.syncHa();
      return true;
    }
    return false;
  }

  /* a bridge streams to one entertainment area at a time */
  setArea(area, on) {
    if (on) AREAS.forEach((A) => { this.syncOn[A.id] = A.id === area; });
    else this.syncOn[area] = false;
    this.applyIntensity();
    this.applyTiming();
    const A = AREAS.find((x) => x.id === area);
    this.flashStatus(on ? (area === 'living_room' ? 'Music sync on in the lounge' : A.name + ' has the bridge now, so the lounge hands its lights back') : A.name + ': music sync off, the lights go back to their scene');
    this.renderRoomNote();
    this.renderNote();
    this.syncHa();
  }

  /* the song moves to another player, as if it had been started there */
  movePlayer(id) {
    const p = PLAYERS.find((x) => x.id === id) || PLAYERS[0];
    if (p.id === this.playerId) return;
    this.playerId = p.id;
    if (this.demo) this.demo.setFeed(p.src !== 'metadata');
    if (this.card) this.card.setConfig(this.cardConfig());
    this.renderMedia();
    this.renderStatus();
    this.renderNote();
    this.syncHa();
  }

  haMessage(msg) {
    const t = msg.type;
    if (t === 'hue_music_sync/players') return this.playersReply(msg.entity_id);
    if (t === 'hue_music_sync/browse') return this.browse(msg.node_id);
    if (t === 'hue_music_sync/search') return this.search(msg.query);
    if (t === 'hue_music_sync/play') return this.libPlay(msg);
    if (t === 'hue_music_sync/backend') {
      if (msg.set === 'subsonic') return Promise.reject({ code: 'backend_error', message: 'This demo uses the Music Assistant library.' });
      return { active: 'music_assistant', available: { music_assistant: true, subsonic: false } };
    }
    if (t === 'hue_music_sync/tap') { this.tap(msg.group); return null; }
    if (t === 'hue_music_sync/drum') { this.drumUntil = msg.active ? performance.now() + 6000 : 0; return null; }
    return undefined;
  }

  haSubscribe(msg, send) {
    if (msg.type !== 'hue_music_sync/subscribe') return false;
    const sub = { entity: msg.entity_id, send };
    this.subs.add(sub);
    /* the slow picture straight away, so the room map and timeline fill in */
    const area = this.areaOn();
    if (area && entsOf(area).sw === msg.entity_id) setTimeout(() => { if (this.subs.has(sub)) send(this.metaEvent(area)); }, 0);
    return () => this.subs.delete(sub);
  }

  playersReply(entity) {
    const area = (this.entIndex[entity] || { area: 'living_room' }).area, tr = this.track();
    const players = PLAYERS.map((p) => ({
      entity_id: p.id, name: p.name, state: this.playerState(p),
      title: p.id === this.playerId ? tr.label : null, artist: p.id === this.playerId ? tr.artist : null, music_assistant: p.mass
    })).sort((x, y) => (Number(!x.music_assistant) - Number(!y.music_assistant)) || (Number(x.state !== 'playing') - Number(y.state !== 'playing')) || x.name.localeCompare(y.name));
    return { players, selected: this.pinned, following: this.syncOn[area] ? this.playerId : null };
  }

  /* the library: Music Assistant's, over the demo's six songs */
  libTrack(t) { return { id: 'track:' + t.id, title: t.label, subtitle: t.artist, kind: 'track', artwork: t.cover, duration: (this.maps[t.id] || {}).duration || null, playable: true, browsable: false }; }
  libAlbum(t) { return { id: 'album:' + t.id, title: t.label, subtitle: t.artist, kind: 'album', artwork: t.cover, playable: true, browsable: true }; }
  libArtist(t) { return { id: 'artist:' + t.id, title: t.artist, subtitle: '1 album', kind: 'artist', artwork: t.cover, playable: true, browsable: true }; }

  browse(nodeId) {
    const T = this.tracks;
    const node = (id, title, items, all) => ({ backend: 'music_assistant', node: { id, title, items, can_play_all: !!all } });
    if (!nodeId) {
      return node('root', 'Music Assistant', [
        { id: 'dir:artists', title: 'Artists', kind: 'directory', playable: false, browsable: true },
        { id: 'dir:albums', title: 'Albums', kind: 'directory', playable: false, browsable: true },
        { id: 'dir:tracks', title: 'Tracks', kind: 'directory', playable: false, browsable: true },
        { id: 'playlist:mix', title: 'Evening mix', subtitle: T.length + ' tracks', kind: 'playlist', artwork: T[0] && T[0].cover, playable: true, browsable: true }
      ]);
    }
    if (nodeId === 'dir:artists') return node(nodeId, 'Artists', T.map((t) => this.libArtist(t)));
    if (nodeId === 'dir:albums') return node(nodeId, 'Albums', T.map((t) => this.libAlbum(t)));
    if (nodeId === 'dir:tracks') return node(nodeId, 'Tracks', T.map((t) => this.libTrack(t)), true);
    if (nodeId === 'playlist:mix') return node(nodeId, 'Evening mix', T.map((t) => this.libTrack(t)), true);
    const [kind, id] = String(nodeId).split(':'), t = T.find((x) => x.id === id);
    if (t && (kind === 'album' || kind === 'artist')) return node(nodeId, kind === 'album' ? t.label : t.artist, [this.libTrack(t)], true);
    return Promise.reject({ code: 'backend_error', message: 'This section is empty in the demo.' });
  }

  search(q) {
    q = String(q || '').trim().toLowerCase();
    const T = q ? this.tracks.filter((t) => (t.label + ' ' + t.artist).toLowerCase().includes(q)) : [];
    return { backend: 'music_assistant', results: { query: q, artists: T.map((t) => this.libArtist(t)), albums: T.map((t) => this.libAlbum(t)), tracks: T.map((t) => this.libTrack(t)) } };
  }

  libPlay(msg) {
    const [kind, id] = String(msg.item_id || '').split(':');
    const t = kind === 'playlist' ? this.tracks[0] : this.tracks.find((x) => x.id === id);
    if (!t) return Promise.reject({ code: 'backend_error', message: 'That item is outside the demo library.' });
    if (msg.enqueue && msg.enqueue !== 'play') return null;
    if (msg.player && msg.player !== this.playerId && PLAYERS.some((p) => p.id === msg.player)) this.movePlayer(msg.player);
    this.selectTrack(this.tracks.indexOf(t), true);
    return null;
  }

  /* a beat pad: a third of the room, by its place in the spectrum */
  tap(group) {
    const area = this.areaOn();
    if (!area) return;
    const n = LAMPS[area].length, a = Math.round(n / 3), b = Math.round((2 * n) / 3);
    let idx = [];
    for (let i = 0; i < n; i++) idx.push(i);
    if (n >= 3) idx = group === 'low' ? idx.slice(0, a) : group === 'mid' ? idx.slice(a, b) : idx.slice(b);
    idx.forEach((i) => { this.lamp[i].tap = 1; });
    this.drumUntil = Math.max(this.drumUntil, performance.now() + 6000);
  }

  /* ================= the engine ================= */

  onDemoState(s) {
    if (s.tick) {
      this.bands = s.bands || null;
      this.energy = s.energy || 0;
      if (s.kick) {
        this.kickIn = true;
        this.kickStrength = s.strength || 0.6;
        this.beatLatch = true;
        this.strengthLatch = Math.max(this.strengthLatch, s.strength || 0.6);
      }
      if (s.level) {
        const lv = String(s.level).toLowerCase();
        if (RUNG[lv] && lv !== this.level) {
          this.level = lv;
          const area = this.areaOn();
          if (area && this.set[area].intensity === 'auto') this.syncHa();
        }
      }
    }
    if (s.status !== undefined) { this.engineStatus = s.status; this.renderStatus(); }
    if (s.loading !== undefined && !!s.loading !== this.loading) { this.loading = !!s.loading; this.renderMedia(); }
    if (s.playing !== undefined && !!s.playing !== this.playing) {
      this.playing = !!s.playing;
      const m = this.demo && this.demo.media();
      this.anchor = { position: m ? Math.round(m.t * 100) / 100 : 0, at: Date.now() };
      this.renderMedia();
      this.renderStatus();
      this.syncHa();
    }
  }

  togglePlay() { if (this.demo) this.demo.toggle(); }

  selectTrack(i, play) {
    const n = this.tracks.length;
    i = ((i % n) + n) % n;
    if (i !== this.trackIdx) {
      this.trackIdx = i;
      if (this.demo) this.demo.select(i);
      this.anchor = { position: 0, at: Date.now() };
    }
    if (play && this.demo && !this.demo.playing) this.demo.play();
    this.renderMedia();
    this.renderStatus();
    this.syncHa();
  }

  /* ================= the page's own cards ================= */

  /* HA's media control card: the artwork on the right, blended into a block
     of its own left-edge colour, with text that stays readable on it */
  renderMedia() {
    const tr = this.track(), P = this.cur(), R = this.refs;
    if (!tr) return;
    this.tv.setTrack(tr, P.name + (P.mass ? ' · Music Assistant' : ''));
    if (R.mcSource) R.mcSource.textContent = P.name;
    if (R.mcIc) R.mcIc.setAttribute('icon', P.icon);
    if (R.queueWho) R.queueWho.textContent = P.name;
    if (R.npTitle) R.npTitle.textContent = tr.label;
    if (R.npArtist) R.npArtist.textContent = tr.artist;
    if (R.mcImg && this.mcArt !== tr.cover) {
      this.mcArt = tr.cover;
      R.mcImg.style.backgroundImage = 'url("' + tr.cover + '")';
      this.artColour(tr.cover).then((c) => {
        if (this.mcArt !== tr.cover || !R.mc) return;
        const lum = (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255;
        R.mc.style.setProperty('--mc-bg', 'rgb(' + c.map(Math.round).join(',') + ')');
        R.mc.style.setProperty('--mc-fg', lum > 0.56 ? '#141414' : '#ffffff');
      });
    }
    if (R.mcIcon) R.mcIcon.setAttribute('icon', this.playing ? 'mdi:pause' : 'mdi:play');
    if (R.mcPlay) {
      R.mcPlay.setAttribute('aria-label', this.playing ? 'Pause' : 'Play');
      R.mcPlay.classList.toggle('is-loading', this.loading);
    }
    if (R.queueNext) {
      const nx = this.tracks[(this.trackIdx + 1) % this.tracks.length];
      R.queueNext.innerHTML = 'Up next: <b></b> · <span></span>';
      R.queueNext.querySelector('b').textContent = nx.label;
      R.queueNext.querySelector('span').textContent = nx.artist;
    }
    if (R.tracks) R.tracks.querySelectorAll('[data-i]').forEach((b) => {
      const on = +b.getAttribute('data-i') === this.trackIdx;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  /* the colour of the artwork's left edge, where the card blends into it */
  artColour(url) {
    this.artCache = this.artCache || {};
    if (this.artCache[url]) return this.artCache[url];
    this.artCache[url] = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = 24; c.height = 24;
          const ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, 24, 24);
          const d = ctx.getImageData(0, 0, 24, 24).data;
          const buckets = new Map();
          for (let i = 0; i < d.length; i += 4) {
            const key = ((d[i] >> 5) << 6) | ((d[i + 1] >> 5) << 3) | (d[i + 2] >> 5);
            let e = buckets.get(key);
            if (!e) { e = { n: 0, r: 0, g: 0, b: 0 }; buckets.set(key, e); }
            e.n++; e.r += d[i]; e.g += d[i + 1]; e.b += d[i + 2];
          }
          let best = null, bestScore = -1;
          buckets.forEach((e) => {
            const r = e.r / e.n, g = e.g / e.n, b = e.b / e.n, mx = Math.max(r, g, b), mn = Math.min(r, g, b);
            const sat = mx ? (mx - mn) / mx : 0, v = mx / 255;
            const score = e.n * (0.35 + sat) * (v < 0.12 ? 0.3 : 1);
            if (score > bestScore) { bestScore = score; best = [r, g, b]; }
          });
          resolve(best || [40, 40, 40]);
        } catch (e) { resolve([40, 40, 40]); }
      };
      img.onerror = () => resolve([40, 40, 40]);
      img.src = url;
    });
    return this.artCache[url];
  }

  renderProgress() {
    if (!this.demo || !this.refs.mcProg) return;
    const m = this.demo.media();
    this.refs.mcProg.style.width = m && m.d ? (m.t / m.d * 100).toFixed(2) + '%' : '0%';
  }

  renderStatus() {
    const el = this.refs.status;
    if (!el) return;
    if (this.statusFlash) { el.textContent = this.statusFlash; return; }
    const tr = this.track(), P = this.cur();
    let t;
    if (this.engineStatus && /blocked|Could not|failed/.test(this.engineStatus)) t = this.engineStatus;
    else if (this.playing) t = 'Playing “' + tr.label + '” on ' + P.name;
    else if (this.demo && this.demo.media() && this.demo.media().t > 0.5) t = 'Paused on ' + P.name;
    else t = 'Press play to start the show';
    el.textContent = t;
  }

  /* a change, said for three seconds the way HA's logbook would */
  flashStatus(text) {
    this.statusFlash = text;
    this.renderStatus();
    clearTimeout(this.statusT);
    this.statusT = setTimeout(() => { this.statusFlash = null; this.renderStatus(); }, 3200);
  }

  renderRoomNote() {
    const el = this.refs.roomNote;
    if (!el) return;
    const area = this.areaOn();
    el.textContent = area === 'living_room' ? 'Hue entertainment area · 7 lights' : area ? 'Lights back to their scene · the bridge is streaming to the ' + AREAS.find((A) => A.id === area).name.toLowerCase() : 'Music sync off · 7 lights in their scene';
  }

  /* what the card cannot say about itself: what the rung does, the warning
     that goes with it, and how the lights hear the chosen player */
  renderNote() {
    const el = this.refs.note;
    if (!el) return;
    const area = this.areaOn() || 'living_room', S = this.set[area], P = this.cur();
    const k = S.intensity, name = TR.mode[k];
    let html = S.effect !== 'music' ? '<b>' + TR.effect[S.effect] + ':</b> ' + EFFECT_COPY[S.effect] : '<b>' + name + ':</b> ' + INT_COPY[k];
    const w = WARN[k];
    if (w && S.effect === 'music') html += ' <span class="is-warn">' + w + (this.reduced ? ' Your device asks for reduced motion, so the room here holds back the flashes.' : '') + '</span>';
    html += '<br><b>' + P.pill + ':</b> ' + (this.pinned ? P.copy : P.id === PLAYERS[0].id ? AUTO_COPY : P.copy);
    el.innerHTML = html;
  }

  /* ---------- the score ---------- */

  fitScore() {
    const c = this.refs.score;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1), r = c.getBoundingClientRect();
    c.width = Math.max(1, Math.round(r.width * dpr)); c.height = Math.max(1, Math.round(r.height * dpr)); c._dpr = dpr;
  }

  wireScoreSeek() {
    const c = this.refs.score;
    if (!c) return;
    this.on(c, 'pointerdown', (ev) => {
      const r = c.getBoundingClientRect(), pad = 12;
      this.sT = Math.max(0, Math.min(SONG.len - 0.01, ((ev.clientX - r.left - pad) / Math.max(1, r.width - pad * 2)) * SONG.len));
      this.sFlashes = []; this.sEnvTrack = energyAt(this.sT) * 0.5;
      this.drawScore();
    });
  }

  stepScore(dt) {
    const t0 = this.sT, t1 = t0 + dt;
    this.sT = t1;
    if (this.sT >= SONG.len) {
      this.sT = SONG.len; this.sPlay = false;
      if (this.refs.scoreBtn) { this.refs.scoreBtn.textContent = '▶ Play again'; this.refs.scoreBtn.classList.remove('is-on'); }
    }
    const sched = this.sMode === 'scheduled';
    const e = energyAt(this.sT);
    this.sEnvTrack += (e - this.sEnvTrack) * (1 - Math.pow(0.5, dt / 1.8));
    const lag = sched ? 0 : REACT_LAG;
    const first = Math.floor((t0 - lag) / SONG.beat) + 1, last = Math.floor((t1 - lag) / SONG.beat);
    for (let k = Math.max(0, first); k <= last; k++) {
      const bt = k * SONG.beat;
      if (bt >= SONG.len) break;
      const down = k % 4 === 0;
      const strength = (sched ? energyAt(bt) : this.sEnvTrack) * (down ? 1 : 0.72);
      this.sFlashes.push([bt + lag, strength]);
      for (let i = 0; i < 9; i++) {
        const role = i % 3, w = role === 0 ? (down ? 1 : 0.55) : role === 1 ? 0.8 : (down ? 0.6 : 0.9);
        this.sLamps[i] = Math.max(this.sLamps[i], Math.min(1, strength * w * (0.85 + Math.random() * 0.3)));
      }
    }
    const s = sectionAt(this.sT);
    const nextHot = SECTIONS.find((x) => x.a > this.sT - 0.001 && x.hot);
    const pre = sched && nextHot && nextHot.a - this.sT < 2.5 && !s.hot ? 1 - (nextHot.a - this.sT) / 2.5 : 0;
    const floor = (sched ? energyAt(this.sT) : this.sEnvTrack) * 0.22 + pre * 0.35;
    const dec = Math.pow(0.5, dt / 0.12);
    for (let i = 0; i < 9; i++) this.sLamps[i] = Math.max(floor, this.sLamps[i] * dec);
    this.sPre = pre;
    this.paintScoreLamps();
  }

  paintScoreLamps() {
    const host = this.refs.scoreLamps;
    if (!host) return;
    const s = sectionAt(this.sT);
    const col = s.hot ? [236, 120, 60] : s.name === 'Build' ? [240, 196, 146] : [228, 166, 103];
    const spans = host.children;
    for (let i = 0; i < spans.length; i++) {
      const v = this.sLamps[i] || 0, r = Math.round(col[0] * v), g = Math.round(col[1] * v), b = Math.round(col[2] * v);
      spans[i].style.background = 'radial-gradient(circle at 40% 35%, rgb(' + Math.min(255, r + 60 * v) + ',' + Math.min(255, g + 60 * v) + ',' + Math.min(255, b + 60 * v) + '), rgb(' + r + ',' + g + ',' + b + ') 70%)';
      spans[i].style.boxShadow = v > 0.08 ? '0 0 ' + Math.round(8 + 22 * v) + 'px rgba(' + r + ',' + g + ',' + b + ',0.65)' : 'none';
    }
  }

  drawScore() {
    const c = this.refs.score;
    if (!c) return;
    const ctx = c.getContext('2d'), W = c.width, H = c.height, s = c._dpr || 1;
    ctx.clearRect(0, 0, W, H);
    const pad = 12 * s, x0 = pad, x1 = W - pad, top = 22 * s, base = H - 24 * s;
    const X = (t) => x0 + (x1 - x0) * (t / SONG.len);
    ctx.font = '600 ' + Math.round(10.5 * s) + 'px Inter, sans-serif';
    ctx.textBaseline = 'top';
    SECTIONS.forEach((sec, i) => {
      if (sec.hot) { const g = ctx.createLinearGradient(0, top, 0, base); g.addColorStop(0, 'rgba(228,166,103,0.13)'); g.addColorStop(1, 'rgba(228,166,103,0.02)'); ctx.fillStyle = g; ctx.fillRect(X(sec.a), top, X(sec.b) - X(sec.a), base - top); }
      ctx.fillStyle = sec.hot ? '#E4A667' : 'rgba(255,255,255,0.5)';
      ctx.textAlign = 'left';
      ctx.fillText(sec.name.toUpperCase(), X(sec.a) + 5 * s, 5 * s);
      if (i) { ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.fillRect(X(sec.a), top, 1 * s, base - top); }
    });
    const n = 110, bw = (x1 - x0) / n;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n * SONG.len, e = energyAt(t) * (0.82 + 0.18 * Math.sin(i * 1.9)), h = (base - top) * e;
      const past = t <= this.sT;
      const g = ctx.createLinearGradient(0, base - h, 0, base);
      g.addColorStop(0, past ? 'rgba(240,190,138,0.95)' : 'rgba(228,166,103,0.32)');
      g.addColorStop(1, past ? 'rgba(200,110,60,0.55)' : 'rgba(228,166,103,0.08)');
      ctx.fillStyle = g;
      const xx = x0 + i * bw + bw * 0.18, ww = bw * 0.64, r = Math.min(ww / 2, 3 * s);
      ctx.beginPath(); ctx.roundRect ? ctx.roundRect(xx, base - h, ww, h, [r, r, 0, 0]) : ctx.rect(xx, base - h, ww, h); ctx.fill();
    }
    ctx.fillStyle = 'rgba(255,255,255,0.26)';
    for (let k = 0; k * SONG.beat < SONG.len; k++) { const x = X(k * SONG.beat), down = k % 4 === 0; ctx.fillRect(x, base + 3 * s, 1 * s, down ? 7 * s : 4 * s); }
    for (const f of this.sFlashes) {
      const x = X(f[0]);
      ctx.fillStyle = this.sMode === 'scheduled' ? 'rgba(61,220,132,0.95)' : 'rgba(240,96,90,0.95)';
      ctx.fillRect(x - 1 * s, base + 12 * s, 2 * s, 8 * s * Math.max(0.35, f[1]));
    }
    const px = X(this.sT);
    ctx.save();
    ctx.fillStyle = '#FFE2C2'; ctx.shadowColor = 'rgba(240,190,138,0.9)'; ctx.shadowBlur = 14 * s;
    ctx.fillRect(px - 1 * s, top - 4 * s, 2 * s, base - top + 8 * s);
    ctx.restore();
    if (this.sPre > 0) {
      const next = SECTIONS.find((x) => x.a > this.sT && x.hot);
      if (next) {
        ctx.strokeStyle = 'rgba(228,166,103,' + (0.3 + 0.6 * this.sPre) + ')';
        ctx.lineWidth = 2 * s;
        ctx.strokeRect(X(next.a) + 1 * s, top + 1 * s, X(next.b) - X(next.a) - 2 * s, base - top - 2 * s);
      }
    }
  }
}

HueSyncoPage.ACTIONS = {
  toggle() { this.togglePlay(); },
  prevTrack() { this.selectTrack(this.trackIdx - 1); },
  nextTrack() { this.selectTrack(this.trackIdx + 1); },
  pickTrack(el) { this.selectTrack(+el.getAttribute('data-i'), true); },
  pickScoreMode(el) {
    this.sMode = el.getAttribute('data-sm');
    this.refs.scoreMode.querySelectorAll('[data-sm]').forEach((b) => b.classList.toggle('is-on', b === el));
    this.sFlashes = [];
    if (this.refs.scoreCopy) {
      this.refs.scoreCopy.textContent = this.sMode === 'scheduled'
        ? 'Scheduled: each flash is placed on the beat grid ahead of time, and the room swells into the drop because it knows the drop is coming.'
        : 'Reacting to live sound: every flash lands a moment after its beat, and the show reaches the drop only once it hears it.';
    }
    this.drawScore();
  },
  toggleScore(el) {
    if (this.sPlay) { this.sPlay = false; el.textContent = '▶ Play'; el.classList.remove('is-on'); return; }
    if (this.sT >= SONG.len - 0.01) { this.sT = 0; this.sFlashes = []; this.sEnvTrack = 0.3; }
    this.sPlay = true; this.last = performance.now();
    el.textContent = '❚❚ Pause'; el.classList.add('is-on');
  }
};

function boot(root) {
  if (!root || INSTANCES.has(root)) return;
  const raw = root.querySelector('[data-huesynco-config]');
  if (!raw) return;
  let cfg;
  try { cfg = JSON.parse(raw.textContent); } catch (e) { console.error('[hue-synco] bad config', e); return; }
  const page = new HueSyncoPage(root, cfg);
  INSTANCES.set(root, page);
  page.mount();
}
function bootAll(scope) { (scope || document).querySelectorAll('[data-huesynco]').forEach(boot); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => bootAll());
else bootAll();
document.addEventListener('shopify:section:load', (e) => bootAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-huesynco]').forEach((root) => { const inst = INSTANCES.get(root); if (inst) { inst.destroy(); INSTANCES.delete(root); } });
});
